import { useEffect, useState, useRef, useCallback } from 'react';
import { useOutletContext, useSearchParams, useNavigate } from 'react-router-dom';
import { getAdminConfig, getOneDriveAuthUrl, listOneDriveFolders, selectOneDriveFolder, syncOneDrive, oneDriveCallback, getOneDriveSyncStatus, getAzureConfig, saveAzureConfig, testAzureConnection, disconnectOneDrive, getCallsProcessingStatus, processPendingCalls, retryFailedCalls, getFolderInfo, getCalls, stopProcessing, hardStopProcessing, resumeProcessing, clearProcessingQueue, getProcessingStatus, diagnoseOneDrive, forceRefreshOneDriveToken, repairFolderSelection, getOneDriveSyncHistory, clearOneDriveSyncHistory, getGuardianHealth, getGuardianMetrics, triggerGuardianRepair, getQuarantinedCalls, unquarantineCall, getGuardianCallAuditTrail, getFolderSyncStatus, resetStuckCalls, clearStopFlag, getDebugFlags, getNetworkDiagnostics, getOneDriveDashboardStatus } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Cloud, 
  Folder, 
  FolderOpen,
  ChevronRight,
  Check,
  RefreshCw,
  AlertCircle,
  ExternalLink,
  Loader2,
  FileAudio,
  Clock,
  CheckCircle2,
  StopCircle,
  XCircle,
  Cpu,
  Play,
  Settings,
  Key,
  Save,
  TestTube,
  Eye,
  EyeOff,
  Unlink,
  HardDrive,
  Files,
  Info,
  Phone,
  AlertTriangle,
  History,
  Trash2,
  ChevronLeft,
  ChevronDown,
  BarChart3,
  Timer,
  FileX,
  Shield,
  HeartPulse,
  Activity,
  RotateCcw,
  Archive,
  Zap,
  Monitor
} from 'lucide-react';
import { ProcessingStatusViewer } from '@/components/ProcessingStatusViewer';
import { PipelineTrace } from '@/components/PipelineTrace';
import PipelineBotWidget from '@/components/PipelineBotWidget';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart } from 'recharts';

export default function AdminOneDrive() {
  // Handle both legacy admin and SaaS tenant contexts
  const outletContext = useOutletContext() || {};
  const adminUrl = outletContext.adminUrl;
  const [config, setConfig] = useState(null);
  const [folders, setFolders] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Import useAuth to check feature restrictions
  const { isFeatureRestricted } = require('@/contexts/AuthContext').useAuth();
  const isAzureConfigRestricted = isFeatureRestricted('azure_config');
  
  // Azure configuration state
  const [azureConfig, setAzureConfig] = useState({
    client_id: '',
    client_secret: '',
    tenant_id: '',
    redirect_uri: ''
  });
  const [azureConfigLoaded, setAzureConfigLoaded] = useState(null);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [activeTab, setActiveTab] = useState('connection');
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // UI State for collapsible sections
  const [showSyncStats, setShowSyncStats] = useState(false);
  const [showTroubleshoot, setShowTroubleshoot] = useState(false);
  
  // Processing Status Viewer state
  const [showProcessingViewer, setShowProcessingViewer] = useState(false);
  const [isProcessingViewerMinimized, setIsProcessingViewerMinimized] = useState(false);
  
  // Folder sync status state (for color-coded folder view)
  const [folderSyncStatus, setFolderSyncStatus] = useState({});
  
  // Folder info dialog state
  const [folderInfoDialog, setFolderInfoDialog] = useState(false);
  const [selectedFolderInfo, setSelectedFolderInfo] = useState(null);
  const [folderToSelect, setFolderToSelect] = useState(null);
  const [isLoadingFolderInfo, setIsLoadingFolderInfo] = useState(false);
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [diagnosisResult, setDiagnosisResult] = useState(null);
  
  // Calls list dialog state
  const [callsListDialog, setCallsListDialog] = useState(false);
  const [callsListFilter, setCallsListFilter] = useState(null); // 'pending', 'processing', 'completed', 'failed'
  const [callsList, setCallsList] = useState([]);
  const [isLoadingCalls, setIsLoadingCalls] = useState(false);
  
  // Sync History state
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncHistorySummary, setSyncHistorySummary] = useState(null);
  const [syncHistoryPagination, setSyncHistoryPagination] = useState({ page: 1, total_pages: 1, total: 0 });
  const [isLoadingSyncHistory, setIsLoadingSyncHistory] = useState(false);
  const [syncHistoryFilter, setSyncHistoryFilter] = useState('all');
  const [expandedSyncId, setExpandedSyncId] = useState(null);

  // Guardian state
  const [guardianHealth, setGuardianHealth] = useState(null);
  const [guardianMetrics, setGuardianMetrics] = useState(null);
  const [quarantinedCalls, setQuarantinedCalls] = useState([]);
  const [isLoadingGuardian, setIsLoadingGuardian] = useState(false);
  const [isRepairingGuardian, setIsRepairingGuardian] = useState(false);
  const [selectedCallAudit, setSelectedCallAudit] = useState(null);
  const [auditTrailDialog, setAuditTrailDialog] = useState(false);
  const [isLoadingAuditTrail, setIsLoadingAuditTrail] = useState(false);
  
  // Processing Health Dashboard state
  const [healthFlags, setHealthFlags] = useState(null);
  const [isLoadingHealthFlags, setIsLoadingHealthFlags] = useState(false);
  const [showHealthDashboard, setShowHealthDashboard] = useState(true);
  
  // Network diagnostics state
  const [networkDiagnostics, setNetworkDiagnostics] = useState(null);
  
  // Processing Speed Graph history (stores last 30 min of data points)
  const [speedHistory, setSpeedHistory] = useState([]);
  const MAX_SPEED_HISTORY = 360; // 30 min at 5s intervals = 360 data points
  
  // Unified dashboard state - single source of truth
  const [dashboard, setDashboard] = useState(null);

  // Handle OAuth callback when returning from Microsoft
  useEffect(() => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    
    if (error) {
      toast.error(`OneDrive authorization failed: ${searchParams.get('error_description') || error}`);
      // Clear URL params
      navigate(window.location.pathname, { replace: true });
      return;
    }
    
    if (code) {
      handleOAuthCallback(code);
    }
  }, [searchParams]);

  const handleOAuthCallback = async (code) => {
    setIsLoading(true);
    try {
      await oneDriveCallback(code);
      toast.success('OneDrive connected successfully!');
      // Clear URL params
      navigate(window.location.pathname, { replace: true });
      // Refresh config
      await fetchConfig();
    } catch (error) {
      console.error('OAuth callback error:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect OneDrive');
      navigate(window.location.pathname, { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch folder sync status for color-coded view
  const fetchFolderSyncStatus = async () => {
    try {
      const { data } = await getFolderSyncStatus();
      setFolderSyncStatus(data.folders || {});
    } catch (error) {
      console.error('Failed to fetch folder sync status:', error);
    }
  };

  useEffect(() => {
    // Only fetch config if not handling OAuth callback
    if (!searchParams.get('code')) {
      fetchConfig();
      fetchSyncStatus();
      fetchAzureConfig();
      fetchProcessingStatus();
      fetchFolderSyncStatus();
      fetchHealthFlags();
      fetchDashboardStatus();
    }
  }, []);

  // Fetch unified dashboard status
  const fetchDashboardStatus = async () => {
    try {
      const { data } = await getOneDriveDashboardStatus();
      setDashboard(data);
      
      // Auto-sync local state with dashboard
      if (data.sync?.in_progress && !isSyncing) {
        setIsSyncing(true);
      } else if (!data.sync?.in_progress && isSyncing) {
        setIsSyncing(false);
      }
    } catch (error) {
      console.error('Dashboard status fetch failed:', error);
    }
  };

  // Poll for sync status while syncing
  useEffect(() => {
    let interval;
    if (isSyncing) {
      interval = setInterval(() => {
        fetchSyncStatus();
        fetchDashboardStatus();
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isSyncing]);

  // Poll for processing status - ALWAYS poll at baseline, faster when processing
  useEffect(() => {
    let interval;
    if (isProcessing || processingStatus?.is_processing) {
      // Fast polling during active processing (every 5 seconds)
      interval = setInterval(() => {
        fetchProcessingStatus();
        fetchDashboardStatus();
      }, 5000);
    } else {
      // Baseline polling every 30 seconds to detect processing started elsewhere
      interval = setInterval(() => {
        fetchProcessingStatus();
        fetchDashboardStatus();
      }, 30000);
    }
    return () => clearInterval(interval);
  }, [isProcessing, processingStatus?.is_processing]);

  // Poll for health flags while processing or when there are issues
  useEffect(() => {
    let interval;
    if (isProcessing || processingStatus?.is_processing || healthFlags?.admin_config_flags?.stop_processing) {
      interval = setInterval(fetchHealthFlags, 10000);
    }
    return () => clearInterval(interval);
  }, [isProcessing, processingStatus?.is_processing, healthFlags?.admin_config_flags?.stop_processing]);

  // Poll for network diagnostics while processing
  useEffect(() => {
    let interval;
    if (isProcessing || processingStatus?.is_processing) {
      fetchNetworkDiagnostics(); // Initial fetch
      interval = setInterval(fetchNetworkDiagnostics, 15000); // Update every 15s
    }
    return () => clearInterval(interval);
  }, [isProcessing, processingStatus?.is_processing]);

  // Fetch sync history when tab changes to history or filter changes
  useEffect(() => {
    if (activeTab === 'history') {
      fetchSyncHistory(1);
    }
  }, [activeTab, syncHistoryFilter]);

  // Fetch guardian data when tab changes to guardian
  useEffect(() => {
    if (activeTab === 'guardian') {
      fetchGuardianData();
    }
  }, [activeTab]);

  // Auto-refresh guardian data every 30 seconds when on guardian tab
  useEffect(() => {
    let interval;
    if (activeTab === 'guardian') {
      interval = setInterval(fetchGuardianData, 30000);
    }
    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchProcessingStatus = async () => {
    try {
      const { data } = await getCallsProcessingStatus();
      setProcessingStatus(data);
      
      const pendingCount = data.queue?.pending || 0;
      const processingCount = data.queue?.processing || 0;
      const needsProcessing = data.needs_processing || 0;
      const backendIsProcessing = data.is_processing || false;
      
      // Auto-sync with backend processing state
      // If backend says it's processing but we don't know yet, sync up
      if (backendIsProcessing && !isProcessing) {
        setIsProcessing(true);
        return;
      }
      
      // Trust the backend's is_processing flag first
      if (backendIsProcessing) {
        return;
      }
      
      // Only show warnings if we LOCALLY thought we were processing
      // AND backend confirms it's NOT processing anymore
      if (isProcessing && !backendIsProcessing && processingCount === 0) {
        if (pendingCount === 0 && needsProcessing === 0) {
          // Truly complete
          setIsProcessing(false);
          if (data.queue?.completed > 0) {
            toast.success('Call processing completed!');
          }
        } else if (pendingCount > 0 || needsProcessing > 0) {
          // Processing stopped with pending calls
          console.warn(`Processing stopped with ${pendingCount} pending, ${needsProcessing} need processing`);
          setIsProcessing(false);
          // Only show warning if there are significant pending calls
          if (pendingCount > 5) {
            toast.warning(`Processing stopped. ${pendingCount} calls still pending. Click "Resume" to continue.`);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch processing status:', error);
    }
  };

  // Fetch health flags for Processing Health Dashboard
  const fetchHealthFlags = async () => {
    setIsLoadingHealthFlags(true);
    try {
      const { data } = await getDebugFlags();
      setHealthFlags(data);
    } catch (error) {
      console.error('Failed to fetch health flags:', error);
    } finally {
      setIsLoadingHealthFlags(false);
    }
  };

  // Fetch network diagnostics for processing monitoring
  const fetchNetworkDiagnostics = async () => {
    try {
      const { data } = await getNetworkDiagnostics();
      setNetworkDiagnostics(data);
      
      // Collect speed data point for the graph
      const callsPerMin = data?.processing_speed?.calls_per_minute;
      const downloadSpeed = data?.last_metrics?.avg_download_speed_kbps;
      if (callsPerMin !== undefined && callsPerMin !== null) {
        const now = new Date();
        const timeLabel = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        setSpeedHistory(prev => {
          const next = [...prev, {
            time: timeLabel,
            ts: now.getTime(),
            callsPerMin: parseFloat(callsPerMin) || 0,
            downloadKbps: parseFloat(downloadSpeed) || 0,
          }];
          // Keep only last MAX_SPEED_HISTORY entries
          return next.length > MAX_SPEED_HISTORY ? next.slice(-MAX_SPEED_HISTORY) : next;
        });
      }
    } catch (error) {
      console.error('Failed to fetch network diagnostics:', error);
    }
  };

  // Auto-diagnose processing issues based on flags
  const getDiagnosis = () => {
    if (!healthFlags) return null;
    
    const issues = [];
    const recommendations = [];
    
    // Check stop_processing flag
    if (healthFlags.admin_config_flags?.stop_processing) {
      issues.push({
        severity: 'critical',
        message: 'Stop flag is active - processing will not continue',
        icon: '🛑'
      });
      recommendations.push('Click "Clear Stop Flag & Reset" to resume processing');
    }
    
    // Check processing_active flag
    if (healthFlags.admin_config_flags?.processing_active && !processingStatus?.is_processing) {
      issues.push({
        severity: 'warning',
        message: 'Processing flag shows active but no processing detected',
        icon: '⚠️'
      });
      recommendations.push('Processing may have crashed - try clearing flags and restarting');
    }
    
    // Check in-memory state mismatch
    if (healthFlags.in_memory_state?.is_processing !== healthFlags.admin_config_flags?.processing_active) {
      issues.push({
        severity: 'info',
        message: 'Memory and database states are out of sync',
        icon: 'ℹ️'
      });
    }
    
    // Check processing job status
    if (healthFlags.processing_job?.status === 'failed') {
      issues.push({
        severity: 'critical',
        message: `Processing job failed: ${healthFlags.processing_job?.error || 'Unknown error'}`,
        icon: '❌'
      });
      recommendations.push('Check backend logs for detailed error information');
    }
    
    // Check if stuck
    if (healthFlags.processing_job?.status === 'running' && !processingStatus?.is_processing) {
      issues.push({
        severity: 'warning',
        message: 'Job status shows running but processing appears stalled',
        icon: '⏸️'
      });
      recommendations.push('Try Hard Stop then Clear & Reset');
    }
    
    // All good
    if (issues.length === 0) {
      return {
        status: 'healthy',
        message: 'All systems operational',
        issues: [],
        recommendations: []
      };
    }
    
    // Determine overall status
    const hasCritical = issues.some(i => i.severity === 'critical');
    const hasWarning = issues.some(i => i.severity === 'warning');
    
    return {
      status: hasCritical ? 'critical' : hasWarning ? 'warning' : 'info',
      message: `${issues.length} issue(s) detected`,
      issues,
      recommendations
    };
  };

  const handleProcessCalls = async () => {
    setIsProcessing(true);
    try {
      const { data } = await processPendingCalls();
      toast.success(data.message);
      // Show the processing status viewer automatically
      setShowProcessingViewer(true);
      setIsProcessingViewerMinimized(false);
      fetchProcessingStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start processing');
      setIsProcessing(false);
    }
  };

  const handleRetryFailed = async () => {
    setIsProcessing(true);
    try {
      const { data } = await retryFailedCalls();
      toast.success(data.message);
      // Show the processing status viewer automatically
      setShowProcessingViewer(true);
      setIsProcessingViewerMinimized(false);
      fetchProcessingStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retry failed calls');
      setIsProcessing(false);
    }
  };

  // Handle folder selection with info dialog
  const handleSelectFolderWithInfo = async (folder) => {
    setFolderToSelect(folder);
    setIsLoadingFolderInfo(true);
    setFolderInfoDialog(true);
    
    try {
      const { data } = await getFolderInfo(folder.id);
      setSelectedFolderInfo(data);
    } catch (error) {
      console.error('Failed to get folder info:', error);
      setSelectedFolderInfo({ error: 'Failed to load folder info' });
    } finally {
      setIsLoadingFolderInfo(false);
    }
  };

  const confirmFolderSelection = async () => {
    if (!folderToSelect) return;
    
    try {
      const fullPath = [...currentPath.map(p => p.name), folderToSelect.name].join('/');
      await selectOneDriveFolder(folderToSelect.id, fullPath);
      await fetchConfig();
      toast.success(`Selected folder: ${fullPath || folderToSelect.name}`);
      setFolderInfoDialog(false);
      setFolderToSelect(null);
      setSelectedFolderInfo(null);
    } catch (error) {
      toast.error('Failed to select folder');
    }
  };

  // Handle clicking on processing status cards
  const handleStatusCardClick = async (status) => {
    setCallsListFilter(status);
    setCallsListDialog(true);
    setIsLoadingCalls(true);
    
    try {
      const { data } = await getCalls({ 
        processing_status: status,
        limit: 50,
        sort_by: 'call_date',
        sort_order: 'desc'
      });
      setCallsList(data.calls || []);
    } catch (error) {
      console.error('Failed to fetch calls:', error);
      setCallsList([]);
    } finally {
      setIsLoadingCalls(false);
    }
  };

  // Handle stop processing button
  const handleStopProcessing = async () => {
    try {
      await stopProcessing();
      // Also clear the stop flag so it can be resumed
      await clearStopFlag();
      setIsProcessing(false);
      toast.success('Stop signal sent. Processing will stop after current batch completes.');
      // Refresh processing status
      fetchProcessingStatus();
    } catch (error) {
      console.error('Failed to stop processing:', error);
      toast.error('Failed to stop processing');
    }
  };

  // Handle HARD stop processing button - immediate stop
  const handleHardStopProcessing = async () => {
    try {
      await hardStopProcessing();
      // Also clear flags and reset stuck calls
      await clearStopFlag();
      await resetStuckCalls();
      setIsProcessing(false);
      toast.success('Hard stop initiated! Processing stopped. You can resume anytime.');
      // Refresh processing status
      fetchProcessingStatus();
    } catch (error) {
      console.error('Failed to hard stop processing:', error);
      toast.error('Failed to hard stop processing: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Handle resume processing
  const handleResumeProcessing = async () => {
    try {
      // Clear stop flag first
      await clearStopFlag();
      const response = await resumeProcessing();
      toast.success(response.data.message || 'Processing resumed');
      setIsProcessing(true);
      // Refresh processing status
      fetchProcessingStatus();
    } catch (error) {
      console.error('Failed to resume processing:', error);
      toast.error(error.response?.data?.message || 'Failed to resume processing');
    }
  };

  // Handle clear queue - remove all pending items to start fresh
  const handleClearQueue = async () => {
    if (!window.confirm('Are you sure you want to clear the sync queue? This will remove all pending calls and allow you to start fresh with a new sync.')) {
      return;
    }
    
    try {
      const response = await clearProcessingQueue();
      toast.success(response.data.message || 'Queue cleared successfully');
      // Refresh processing status
      fetchProcessingStatus();
      fetchSyncStatus();
    } catch (error) {
      console.error('Failed to clear queue:', error);
      toast.error(error.response?.data?.detail || 'Failed to clear queue');
    }
  };

  // Fetch sync history
  const fetchSyncHistory = async (page = 1) => {
    setIsLoadingSyncHistory(true);
    try {
      const params = { page, limit: 15 };
      if (syncHistoryFilter !== 'all') {
        params.status = syncHistoryFilter;
      }
      const { data } = await getOneDriveSyncHistory(params);
      setSyncHistory(data.history || []);
      setSyncHistorySummary(data.summary || null);
      setSyncHistoryPagination(data.pagination || { page: 1, total_pages: 1, total: 0 });
    } catch (error) {
      console.error('Failed to fetch sync history:', error);
    } finally {
      setIsLoadingSyncHistory(false);
    }
  };

  // Clear old sync history
  const handleClearSyncHistory = async (days = 30) => {
    if (!window.confirm(`Are you sure you want to clear sync history older than ${days} days?`)) {
      return;
    }
    
    try {
      const response = await clearOneDriveSyncHistory(days);
      toast.success(response.data.message || 'Sync history cleared');
      fetchSyncHistory(1);
    } catch (error) {
      console.error('Failed to clear sync history:', error);
      toast.error('Failed to clear sync history');
    }
  };

  // Format duration from seconds
  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  // Format date for display
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ==================== GUARDIAN FUNCTIONS ====================
  
  const fetchGuardianData = async () => {
    setIsLoadingGuardian(true);
    try {
      const [healthRes, metricsRes, quarantinedRes] = await Promise.all([
        getGuardianHealth(),
        getGuardianMetrics(),
        getQuarantinedCalls(0, 100)
      ]);
      
      setGuardianHealth(healthRes.data);
      setGuardianMetrics(metricsRes.data);
      setQuarantinedCalls(quarantinedRes.data.quarantined_calls || []);
    } catch (error) {
      console.error('Failed to fetch guardian data:', error);
      toast.error('Failed to load guardian data');
    } finally {
      setIsLoadingGuardian(false);
    }
  };

  const handleTriggerRepair = async () => {
    setIsRepairingGuardian(true);
    try {
      await triggerGuardianRepair();
      toast.success('Repair cycle initiated');
      // Refresh guardian data after a short delay
      setTimeout(fetchGuardianData, 2000);
    } catch (error) {
      console.error('Failed to trigger repair:', error);
      toast.error('Failed to trigger repair');
    } finally {
      setIsRepairingGuardian(false);
    }
  };

  const handleUnquarantineCall = async (callId) => {
    try {
      await unquarantineCall(callId);
      toast.success('Call removed from quarantine and queued for retry');
      fetchGuardianData();
    } catch (error) {
      console.error('Failed to unquarantine call:', error);
      toast.error(error.response?.data?.detail || 'Failed to unquarantine call');
    }
  };

  const handleViewAuditTrail = async (callId) => {
    setIsLoadingAuditTrail(true);
    setAuditTrailDialog(true);
    try {
      const { data } = await getGuardianCallAuditTrail(callId);
      setSelectedCallAudit(data);
    } catch (error) {
      console.error('Failed to fetch audit trail:', error);
      toast.error('Failed to load audit trail');
    } finally {
      setIsLoadingAuditTrail(false);
    }
  };

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'bg-emerald-500';
      case 'degraded': return 'bg-amber-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-slate-500';
    }
  };

  const getHealthStatusBadge = (status) => {
    switch (status) {
      case 'healthy': return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">Healthy</Badge>;
      case 'degraded': return <Badge className="bg-amber-100 text-amber-700 border-amber-300">Degraded</Badge>;
      case 'error': return <Badge className="bg-red-100 text-red-700 border-red-300">Error</Badge>;
      default: return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const fetchAzureConfig = async () => {
    try {
      const { data } = await getAzureConfig();
      setAzureConfigLoaded(data);
      // Pre-fill the form with existing values (except secret)
      setAzureConfig({
        client_id: data.client_id || '',
        client_secret: '', // Don't pre-fill secret for security
        tenant_id: data.tenant_id || '',
        redirect_uri: data.redirect_uri || window.location.origin + '/dashboard/onedrive'
      });
    } catch (error) {
      console.error('Failed to fetch Azure config:', error);
    }
  };

  const handleSaveAzureConfig = async () => {
    if (!azureConfig.client_id || !azureConfig.redirect_uri) {
      toast.error('Client ID and Redirect URI are required');
      return;
    }
    
    // If no new secret provided but one exists, don't overwrite
    const configToSave = {
      ...azureConfig,
      client_secret: azureConfig.client_secret || (azureConfigLoaded?.has_client_secret ? undefined : '')
    };
    
    if (!configToSave.client_secret && !azureConfigLoaded?.has_client_secret) {
      toast.error('Client Secret is required');
      return;
    }
    
    setIsSavingConfig(true);
    try {
      // Only send secret if it's been changed
      const payload = {
        client_id: azureConfig.client_id,
        tenant_id: azureConfig.tenant_id || 'common',
        redirect_uri: azureConfig.redirect_uri
      };
      
      if (azureConfig.client_secret) {
        payload.client_secret = azureConfig.client_secret;
      } else if (azureConfigLoaded?.has_client_secret) {
        // Need to fetch the existing secret from backend to resave
        // For security, we'll require re-entering the secret to update
        toast.error('Please re-enter the Client Secret to save changes');
        setIsSavingConfig(false);
        return;
      }
      
      await saveAzureConfig(payload);
      toast.success('Azure configuration saved successfully!');
      await fetchAzureConfig();
    } catch (error) {
      console.error('Failed to save Azure config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    try {
      const { data } = await testAzureConnection();
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Connection test failed:', error);
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setIsTestingConnection(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const { data } = await getOneDriveSyncStatus();
      setSyncStatus(data);
      if (data.sync_in_progress === false && isSyncing) {
        setIsSyncing(false);
        toast.success(`Sync complete! ${data.sync_stats?.files_synced || 0} new files imported.`);
        fetchConfig();
      }
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
    }
  };

  const fetchConfig = async () => {
    try {
      const { data } = await getAdminConfig();
      setConfig(data);
      if (data.onedrive_config?.is_connected) {
        await fetchFolders();
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFolders = async (folderId = null) => {
    try {
      const { data } = await listOneDriveFolders(folderId);
      setFolders(data.filter(item => item.is_folder));
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('OneDrive session expired. Please reconnect.');
        setConfig(prev => ({...prev, onedrive_config: {...prev.onedrive_config, is_connected: false}}));
      } else {
        toast.error('Failed to fetch folders');
      }
    }
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const { data } = await getOneDriveAuthUrl();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error(data.message || 'OneDrive not configured');
      }
    } catch (error) {
      toast.error('Failed to get auth URL');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleFolderClick = async (folder) => {
    setCurrentPath([...currentPath, { id: folder.id, name: folder.name }]);
    await fetchFolders(folder.id);
  };

  const handleBreadcrumbClick = async (index) => {
    if (index === -1) {
      setCurrentPath([]);
      await fetchFolders();
    } else {
      const newPath = currentPath.slice(0, index + 1);
      setCurrentPath(newPath);
      await fetchFolders(newPath[newPath.length - 1].id);
    }
  };

  const handleSelectFolder = async (folder) => {
    // Open folder info dialog instead of directly selecting
    handleSelectFolderWithInfo(folder);
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const userId = outletContext?.user?.id || 'user';
      await syncOneDrive(true, userId);
      toast.success('Sync started! Calls will appear shortly.');
    } catch (error) {
      console.error('Sync error:', error.response?.data);
      const errorMessage = error.response?.data?.detail || 'Failed to start sync';
      toast.error(errorMessage);
      
      // If it's a connection issue, suggest reconnecting
      if (error.response?.status === 401) {
        toast.error('Please try reconnecting OneDrive', { duration: 5000 });
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDiagnose = async () => {
    setIsDiagnosing(true);
    setDiagnosisResult(null);
    try {
      const { data } = await diagnoseOneDrive();
      setDiagnosisResult(data);
      
      if (data.issues && data.issues.length > 0) {
        toast.error(`Found ${data.issues.length} issue(s) - see details below`);
      } else {
        toast.success('OneDrive connection looks healthy!');
      }
    } catch (error) {
      toast.error('Failed to run diagnostics');
      console.error('Diagnosis error:', error);
    } finally {
      setIsDiagnosing(false);
    }
  };

  const handleForceRefresh = async () => {
    try {
      await forceRefreshOneDriveToken();
      toast.success('Token refreshed successfully! Try syncing again.');
      setDiagnosisResult(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Token refresh failed. Please reconnect OneDrive.');
    }
  };

  const handleRepairFolderSelection = async () => {
    try {
      const { data } = await repairFolderSelection();
      toast.success(`Folder selection repaired! ID: ${data.folder_id}`);
      setDiagnosisResult(null);
      await fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to repair folder selection');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect OneDrive? You will need to reconnect and re-authenticate to sync calls again.')) {
      return;
    }
    
    setIsDisconnecting(true);
    try {
      await disconnectOneDrive();
      toast.success('OneDrive disconnected successfully');
      // Reset local state
      setFolders([]);
      setCurrentPath([]);
      setSyncStatus(null);
      // Refresh config
      await fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect OneDrive');
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const isConnected = config?.onedrive_config?.is_connected;
  const selectedFolder = config?.onedrive_config?.selected_folder_path;

  return (
    <div className="space-y-6" data-testid="admin-onedrive">
      {/* Header */}
      <Card className="card-elevated">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-sm ${isConnected ? 'bg-emerald-100' : 'bg-muted'}`}>
                <Cloud className={`h-6 w-6 ${isConnected ? 'text-emerald-600' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <CardTitle className="font-heading">OneDrive Integration</CardTitle>
                <CardDescription>
                  Connect to OneDrive to automatically import call recordings
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isConnected && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDisconnect}
                  disabled={isDisconnecting}
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                >
                  {isDisconnecting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Unlink className="h-4 w-4 mr-2" />
                  )}
                  Disconnect
                </Button>
              )}
              <Badge variant={isConnected ? 'default' : 'secondary'} className={isConnected ? 'bg-emerald-500' : ''}>
                {isConnected ? 'Connected' : 'Not Connected'}
              </Badge>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabs for Connection, History, Guardian, and Configuration */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full ${isAzureConfigRestricted ? 'grid-cols-3' : 'grid-cols-4'}`}>
          <TabsTrigger value="connection" className="flex items-center gap-2">
            <Cloud className="h-4 w-4" />
            Connection
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Sync History
          </TabsTrigger>
          <TabsTrigger value="guardian" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Guardian
            {guardianHealth?.status === 'degraded' && (
              <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
            )}
            {guardianHealth?.status === 'error' && (
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            )}
          </TabsTrigger>
          {!isAzureConfigRestricted && (
            <TabsTrigger value="configuration" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Azure Config
            </TabsTrigger>
          )}
        </TabsList>

        {/* Connection Tab - REDESIGNED FOR CLARITY */}
        <TabsContent value="connection" className="space-y-4">
          
          {/* NOT CONNECTED STATE */}
          {!isConnected ? (
            <Card className="card-elevated">
              <CardContent className="py-12">
                <div className="text-center">
                  <div className="p-4 bg-amber-100 rounded-full w-fit mx-auto mb-4">
                    <Cloud className="h-10 w-10 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Connect to OneDrive</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Connect your OneDrive account to automatically import and process call recordings.
                  </p>
                  {!azureConfigLoaded?.is_configured && (
                    <p className="text-sm text-amber-600 mb-4">
                      ⚠️ Azure credentials not configured. Go to "Azure Config" tab first.
                    </p>
                  )}
                  <Button 
                    onClick={handleConnect} 
                    size="lg"
                    disabled={isConnecting || !azureConfigLoaded?.is_configured}
                    data-testid="connect-onedrive-btn"
                  >
                    {isConnecting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Cloud className="h-5 w-5 mr-2" />}
                    Connect OneDrive
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* ===== UNIFIED OPERATIONS DASHBOARD ===== */}
              
              {/* Status Bar - Connection + Folder */}
              <Card className="card-elevated border-l-4 border-l-emerald-500">
                <CardContent className="py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-emerald-100 rounded-lg">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Connected Folder</p>
                        <p className="font-medium font-mono text-sm">{selectedFolder || 'No folder selected'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {dashboard?.sync?.last_sync && (
                        <span>Last sync: {new Date(dashboard.sync.last_sync).toLocaleString()}</span>
                      )}
                      <Badge variant={dashboard?.health?.is_healthy ? "outline" : "destructive"} className="text-[10px]">
                        {dashboard?.health?.is_healthy ? 'Healthy' : `${dashboard?.health?.issues?.length || 0} Issues`}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Counts Overview - The Source of Truth */}
              {dashboard && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="counts-overview">
                  <Card className="bg-slate-50 border-slate-200">
                    <CardContent className="p-3 text-center">
                      <p className="text-2xl font-bold text-slate-900">{dashboard.counts?.total_synced || 0}</p>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide">Total Synced</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-3 text-center">
                      <p className="text-2xl font-bold text-green-700">{dashboard.counts?.completed || 0}</p>
                      <p className="text-[10px] text-green-600 uppercase tracking-wide">Completed</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-3 text-center">
                      <p className="text-2xl font-bold text-amber-700">{dashboard.counts?.pending || 0}</p>
                      <p className="text-[10px] text-amber-600 uppercase tracking-wide">Pending</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="p-3 text-center">
                      <p className="text-2xl font-bold text-purple-700">{dashboard.counts?.in_processing || 0}</p>
                      <p className="text-[10px] text-purple-600 uppercase tracking-wide">Processing</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-3 text-center">
                      <p className="text-2xl font-bold text-red-700">{dashboard.counts?.failed || 0}</p>
                      <p className="text-[10px] text-red-600 uppercase tracking-wide">Failed</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-3 text-center">
                      <p className="text-2xl font-bold text-blue-700">{dashboard.counts?.queue_pending || 0}</p>
                      <p className="text-[10px] text-blue-600 uppercase tracking-wide">In Queue</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-indigo-50 border-indigo-200">
                    <CardContent className="p-3 text-center">
                      <p className="text-2xl font-bold text-indigo-700">{dashboard.counts?.queue_processing || 0}</p>
                      <p className="text-[10px] text-indigo-600 uppercase tracking-wide">Queue Active</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Health Issues Banner */}
              {dashboard?.health?.issues?.length > 0 && (
                <Card className="border-amber-300 bg-amber-50/50">
                  <CardContent className="py-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                      <div className="space-y-1">
                        {dashboard.health.issues.map((issue, i) => (
                          <p key={i} className="text-xs text-amber-800">{issue}</p>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Real-time Sync Progress */}
              {isSyncing && syncStatus?.sync_progress && (
                <Card className="border-blue-300 bg-blue-50/50">
                  <CardContent className="py-3">
                    <div className="flex items-center gap-3">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-blue-800 truncate">
                          {syncStatus.sync_progress.status_message || 'Syncing...'}
                        </p>
                        <div className="flex gap-4 mt-1 text-xs text-blue-600">
                          <span>{syncStatus.sync_progress.folders_scanned || 0} folders scanned</span>
                          <span>{syncStatus.sync_progress.files_found || 0} files found</span>
                          <span className="font-semibold">{syncStatus.sync_progress.files_synced || 0} new synced</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Actions Panel */}
              <PipelineBotWidget />

              <Card className="card-elevated" data-testid="operations-panel">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-purple-600" />
                      <CardTitle className="text-base">Operations</CardTitle>
                      {(isProcessing || processingStatus?.is_processing || dashboard?.processing?.is_active) && (
                        <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                          </span>
                          Live
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      Auto-refreshing {(isProcessing || processingStatus?.is_processing) ? 'every 5s' : 'every 30s'}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  
                  {/* Primary Action Buttons */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Step 1: Sync */}
                    <Button 
                      onClick={handleSync} 
                      disabled={!selectedFolder || isSyncing}
                      variant="outline"
                      className="border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                      data-testid="sync-btn"
                    >
                      {isSyncing ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      {isSyncing ? 'Syncing...' : '1. Sync Folders'}
                    </Button>
                    
                    {/* Step 2: Process */}
                    <Button 
                      onClick={handleProcessCalls}
                      disabled={isProcessing || processingStatus?.is_processing || dashboard?.processing?.is_active}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="process-calls-btn"
                    >
                      {isProcessing || processingStatus?.is_processing || dashboard?.processing?.is_active ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Play className="h-4 w-4 mr-2" />
                      )}
                      {isProcessing || processingStatus?.is_processing || dashboard?.processing?.is_active ? 'Processing...' : '2. Process Calls'}
                    </Button>
                    
                    {/* Retry Failed */}
                    {(dashboard?.counts?.failed > 0) && (
                      <Button
                        variant="outline"
                        className="border-orange-200 hover:bg-orange-50 hover:text-orange-700"
                        onClick={async () => {
                          try {
                            await retryFailedCalls();
                            toast.success('Retrying failed calls...');
                            setTimeout(fetchDashboardStatus, 2000);
                          } catch (e) {
                            toast.error('Failed to retry: ' + (e.response?.data?.detail || e.message));
                          }
                        }}
                        data-testid="retry-failed-btn"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Retry Failed ({dashboard.counts.failed})
                      </Button>
                    )}

                    {/* Stop Processing */}
                    {(isProcessing || processingStatus?.is_processing || dashboard?.processing?.is_active) && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={async () => {
                          try {
                            await stopProcessing();
                            toast.info('Stop signal sent. Processing will stop after current batch.');
                            setTimeout(fetchDashboardStatus, 3000);
                          } catch (e) {
                            toast.error('Failed to stop: ' + (e.response?.data?.detail || e.message));
                          }
                        }}
                        data-testid="stop-processing-btn"
                      >
                        <StopCircle className="h-4 w-4 mr-2" />
                        Stop
                      </Button>
                    )}

                    {/* View Progress */}
                    {(isProcessing || processingStatus?.is_processing || dashboard?.processing?.is_active) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowProcessingViewer(true);
                          setIsProcessingViewerMinimized(false);
                        }}
                        data-testid="view-progress-btn"
                      >
                        <Monitor className="h-4 w-4 mr-2" />
                        Details
                      </Button>
                    )}
                  </div>

                  {/* Recovery Actions - Only show when needed */}
                  {(dashboard?.health?.issues?.length > 0 || dashboard?.processing?.stop_flag) && (
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-xs text-muted-foreground mb-2 font-medium">Recovery Actions</p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs border-red-200 hover:bg-red-50 text-red-700"
                          onClick={async () => {
                            try {
                              await clearStopFlag();
                              await resetStuckCalls();
                              toast.success('All flags cleared & stuck calls reset');
                              setTimeout(() => {
                                fetchDashboardStatus();
                                fetchProcessingStatus();
                                fetchHealthFlags();
                              }, 1000);
                            } catch (e) {
                              toast.error('Reset failed: ' + (e.response?.data?.detail || e.message));
                            }
                          }}
                          data-testid="clear-flags-btn"
                        >
                          <XCircle className="h-3 w-3 mr-1" />
                          Clear All Flags & Reset
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={async () => {
                            try {
                              await clearProcessingQueue();
                              toast.success('Processing queue cleared');
                              setTimeout(fetchDashboardStatus, 1000);
                            } catch (e) {
                              toast.error('Failed: ' + (e.response?.data?.detail || e.message));
                            }
                          }}
                          data-testid="clear-queue-btn"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Clear Queue
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Processing Progress Bar */}
                  {(isProcessing || processingStatus?.is_processing || dashboard?.processing?.is_active) && (
                    <div className="space-y-2 pt-2 border-t border-slate-100">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
                          </span>
                          Processing...
                        </span>
                        <span className="font-medium">
                          {dashboard?.counts?.completed || processingStatus?.queue?.completed || 0} / {dashboard?.counts?.total_synced || processingStatus?.progress?.total || '?'}
                        </span>
                      </div>
                      <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all duration-500"
                          style={{ 
                            width: `${(() => {
                              const completed = dashboard?.counts?.completed || 0;
                              const total = dashboard?.counts?.total_synced || 1;
                              return total > 0 ? Math.min((completed / total * 100), 100) : 0;
                            })()}%` 
                          }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Updated: {new Date().toLocaleTimeString()}</span>
                        {networkDiagnostics?.processing_speed?.calls_per_minute > 0 && (dashboard?.counts?.pending || 0) > 0 && (
                          <span className="font-medium text-purple-600">
                            ETA: ~{Math.ceil((dashboard.counts.pending) / networkDiagnostics.processing_speed.calls_per_minute)} min
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Network Diagnostics & Speed Graph */}
                  {networkDiagnostics && (isProcessing || processingStatus?.is_processing || dashboard?.processing?.is_active) && (
                    <div className="pt-2 border-t border-slate-100">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                        <div className="bg-slate-50 rounded-lg p-2">
                          <p className="text-muted-foreground">Speed</p>
                          <p className="font-semibold">{networkDiagnostics.processing_speed?.calls_per_minute || 0} calls/min</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2">
                          <p className="text-muted-foreground">Download</p>
                          <p className="font-semibold">{networkDiagnostics.last_metrics?.avg_download_speed_kbps ? `${Math.round(networkDiagnostics.last_metrics.avg_download_speed_kbps)} KB/s` : 'N/A'}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2">
                          <p className="text-muted-foreground">Server IP</p>
                          <p className="font-semibold font-mono">{networkDiagnostics.server_ip || 'N/A'}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2">
                          <p className="text-muted-foreground">Avg Time</p>
                          <p className="font-semibold">{networkDiagnostics.last_metrics?.avg_process_time ? `${Math.round(networkDiagnostics.last_metrics.avg_process_time)}s` : 'N/A'}</p>
                        </div>
                      </div>
                      
                      {/* Processing Speed Graph */}
                      {speedHistory.length > 1 && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-slate-700 flex items-center gap-1">
                              <Activity className="h-3 w-3" />
                              Speed (calls/min)
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              {speedHistory.length} readings
                            </span>
                          </div>
                          <div className="h-[100px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={speedHistory} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                                <defs>
                                  <linearGradient id="speedGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.02} />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                <XAxis dataKey="time" tick={{ fontSize: 9, fill: '#94a3b8' }} interval="preserveStartEnd" tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                                <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} tickLine={false} axisLine={false} domain={[0, 'auto']} />
                                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0', padding: '6px 10px' }} formatter={(value) => [`${value} calls/min`, 'Speed']} />
                                <ReferenceLine y={5} stroke="#22c55e" strokeDasharray="4 4" strokeOpacity={0.6} />
                                <ReferenceLine y={2} stroke="#f59e0b" strokeDasharray="4 4" strokeOpacity={0.6} />
                                <Area type="monotone" dataKey="callsPerMin" stroke="#8b5cf6" strokeWidth={2} fill="url(#speedGradient)" dot={false} activeDot={{ r: 3, fill: '#8b5cf6', strokeWidth: 0 }} isAnimationActive={false} />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pipeline Trace - Call Tracking Diagnostic */}
              <PipelineTrace />

              {/* SECTION 3: Sync Statistics (Collapsible) */}
              {syncStatus && (
                <Card className="card-elevated">
                  <CardHeader className="pb-3 cursor-pointer" onClick={() => setShowSyncStats(!showSyncStats)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileAudio className="h-5 w-5 text-blue-600" />
                        <CardTitle className="text-base">Sync Statistics</CardTitle>
                      </div>
                      <ChevronRight className={`h-5 w-5 text-muted-foreground transition-transform ${showSyncStats ? 'rotate-90' : ''}`} />
                    </div>
                    {config?.onedrive_config?.last_sync && (
                      <CardDescription>
                        Last synced: {new Date(config.onedrive_config.last_sync).toLocaleString()}
                      </CardDescription>
                    )}
                  </CardHeader>
                  {showSyncStats && (
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="p-3 bg-blue-50 rounded-lg text-center">
                          <p className="text-2xl font-bold text-blue-700">{syncStatus.total_synced_calls || 0}</p>
                          <p className="text-xs text-blue-600">Total Synced</p>
                        </div>
                        <div className="p-3 bg-emerald-50 rounded-lg text-center">
                          <p className="text-2xl font-bold text-emerald-700">{syncStatus.sync_stats?.files_synced || 0}</p>
                          <p className="text-xs text-emerald-600">Last Sync Added</p>
                        </div>
                        <div className="p-3 bg-amber-50 rounded-lg text-center">
                          <p className="text-2xl font-bold text-amber-700">{syncStatus.sync_stats?.files_skipped || 0}</p>
                          <p className="text-xs text-amber-600">Skipped</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg text-center">
                          <p className="text-2xl font-bold text-slate-700">{syncStatus.sync_stats?.folders_scanned || 0}</p>
                          <p className="text-xs text-slate-600">Folders</p>
                        </div>
                      </div>
                      
                      {/* Sync Errors */}
                      {syncStatus?.sync_stats?.errors?.length > 0 && (
                        <div className="mt-3 p-3 bg-red-50 rounded-lg">
                          <p className="text-sm font-medium text-red-700 mb-2">
                            ⚠️ {syncStatus.sync_stats.errors.length} Sync Error(s)
                          </p>
                          <ul className="text-xs text-red-600 space-y-1 max-h-20 overflow-y-auto">
                            {syncStatus.sync_stats.errors.slice(0, 3).map((err, i) => (
                              <li key={i}>• {err}</li>
                            ))}
                            {syncStatus.sync_stats.errors.length > 3 && (
                              <li className="text-red-500">...and {syncStatus.sync_stats.errors.length - 3} more</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              )}

              {/* SECTION 3.5: Processing Health Dashboard (Always visible when issues detected) */}
              <Card className={`card-elevated border-l-4 ${
                getDiagnosis()?.status === 'critical' ? 'border-l-red-500 bg-red-50/30' :
                getDiagnosis()?.status === 'warning' ? 'border-l-amber-500 bg-amber-50/30' :
                getDiagnosis()?.status === 'healthy' ? 'border-l-emerald-500' : 'border-l-slate-300'
              }`}>
                <CardHeader className="pb-3 cursor-pointer" onClick={() => setShowHealthDashboard(!showHealthDashboard)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded-lg ${
                        getDiagnosis()?.status === 'critical' ? 'bg-red-100' :
                        getDiagnosis()?.status === 'warning' ? 'bg-amber-100' :
                        getDiagnosis()?.status === 'healthy' ? 'bg-emerald-100' : 'bg-slate-100'
                      }`}>
                        <Cpu className={`h-4 w-4 ${
                          getDiagnosis()?.status === 'critical' ? 'text-red-600' :
                          getDiagnosis()?.status === 'warning' ? 'text-amber-600' :
                          getDiagnosis()?.status === 'healthy' ? 'text-emerald-600' : 'text-slate-600'
                        }`} />
                      </div>
                      <CardTitle className="text-base">Processing Health Dashboard</CardTitle>
                      {getDiagnosis()?.status === 'healthy' ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">Healthy</Badge>
                      ) : getDiagnosis()?.status === 'warning' ? (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-300">{getDiagnosis()?.issues?.length} Warning(s)</Badge>
                      ) : getDiagnosis()?.status === 'critical' ? (
                        <Badge className="bg-red-100 text-red-700 border-red-300">{getDiagnosis()?.issues?.length} Issue(s)</Badge>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchHealthFlags();
                        }}
                        disabled={isLoadingHealthFlags}
                      >
                        <RefreshCw className={`h-4 w-4 ${isLoadingHealthFlags ? 'animate-spin' : ''}`} />
                      </Button>
                      <ChevronRight className={`h-5 w-5 text-muted-foreground transition-transform ${showHealthDashboard ? 'rotate-90' : ''}`} />
                    </div>
                  </div>
                  <CardDescription>
                    Real-time system flags and auto-diagnosis
                  </CardDescription>
                </CardHeader>
                {showHealthDashboard && (
                  <CardContent className="pt-0 space-y-4">
                    {isLoadingHealthFlags && !healthFlags ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : healthFlags ? (
                      <>
                        {/* Flag Status Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {/* Stop Processing Flag */}
                          <div className={`p-3 rounded-lg text-center ${
                            healthFlags.admin_config_flags?.stop_processing ? 'bg-red-100 border border-red-300' : 'bg-emerald-50'
                          }`}>
                            <div className="text-lg mb-1">
                              {healthFlags.admin_config_flags?.stop_processing ? '🛑' : '✅'}
                            </div>
                            <p className={`text-xs font-medium ${
                              healthFlags.admin_config_flags?.stop_processing ? 'text-red-700' : 'text-emerald-700'
                            }`}>
                              Stop Flag
                            </p>
                            <p className={`text-xs ${
                              healthFlags.admin_config_flags?.stop_processing ? 'text-red-600' : 'text-emerald-600'
                            }`}>
                              {healthFlags.admin_config_flags?.stop_processing ? 'ACTIVE' : 'Clear'}
                            </p>
                          </div>
                          
                          {/* Processing Active Flag */}
                          <div className={`p-3 rounded-lg text-center ${
                            healthFlags.admin_config_flags?.processing_active ? 'bg-blue-100' : 'bg-slate-50'
                          }`}>
                            <div className="text-lg mb-1">
                              {healthFlags.admin_config_flags?.processing_active ? '⚙️' : '⏸️'}
                            </div>
                            <p className={`text-xs font-medium ${
                              healthFlags.admin_config_flags?.processing_active ? 'text-blue-700' : 'text-slate-700'
                            }`}>
                              DB Active Flag
                            </p>
                            <p className={`text-xs ${
                              healthFlags.admin_config_flags?.processing_active ? 'text-blue-600' : 'text-slate-600'
                            }`}>
                              {healthFlags.admin_config_flags?.processing_active ? 'Running' : 'Idle'}
                            </p>
                          </div>
                          
                          {/* In-Memory Processing State */}
                          <div className={`p-3 rounded-lg text-center ${
                            healthFlags.in_memory_state?.is_processing ? 'bg-purple-100' : 'bg-slate-50'
                          }`}>
                            <div className="text-lg mb-1">
                              {healthFlags.in_memory_state?.is_processing ? '🔄' : '💤'}
                            </div>
                            <p className={`text-xs font-medium ${
                              healthFlags.in_memory_state?.is_processing ? 'text-purple-700' : 'text-slate-700'
                            }`}>
                              Memory State
                            </p>
                            <p className={`text-xs ${
                              healthFlags.in_memory_state?.is_processing ? 'text-purple-600' : 'text-slate-600'
                            }`}>
                              {healthFlags.in_memory_state?.is_processing ? 'Processing' : 'Idle'}
                            </p>
                          </div>
                          
                          {/* Job Status */}
                          <div className={`p-3 rounded-lg text-center ${
                            healthFlags.processing_job?.status === 'running' ? 'bg-blue-100' :
                            healthFlags.processing_job?.status === 'failed' ? 'bg-red-100' :
                            healthFlags.processing_job?.status === 'completed' ? 'bg-emerald-100' : 'bg-slate-50'
                          }`}>
                            <div className="text-lg mb-1">
                              {healthFlags.processing_job?.status === 'running' ? '▶️' :
                               healthFlags.processing_job?.status === 'failed' ? '❌' :
                               healthFlags.processing_job?.status === 'completed' ? '✅' : '⏹️'}
                            </div>
                            <p className={`text-xs font-medium ${
                              healthFlags.processing_job?.status === 'running' ? 'text-blue-700' :
                              healthFlags.processing_job?.status === 'failed' ? 'text-red-700' :
                              healthFlags.processing_job?.status === 'completed' ? 'text-emerald-700' : 'text-slate-700'
                            }`}>
                              Job Status
                            </p>
                            <p className={`text-xs ${
                              healthFlags.processing_job?.status === 'running' ? 'text-blue-600' :
                              healthFlags.processing_job?.status === 'failed' ? 'text-red-600' :
                              healthFlags.processing_job?.status === 'completed' ? 'text-emerald-600' : 'text-slate-600'
                            }`}>
                              {healthFlags.processing_job?.status || 'None'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Auto-Diagnosis Section */}
                        {getDiagnosis() && getDiagnosis().issues?.length > 0 && (
                          <div className={`p-3 rounded-lg border ${
                            getDiagnosis().status === 'critical' ? 'bg-red-50 border-red-200' :
                            getDiagnosis().status === 'warning' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'
                          }`}>
                            <p className={`text-sm font-medium mb-2 ${
                              getDiagnosis().status === 'critical' ? 'text-red-700' :
                              getDiagnosis().status === 'warning' ? 'text-amber-700' : 'text-blue-700'
                            }`}>
                              🔍 Auto-Diagnosis
                            </p>
                            <ul className="space-y-1 mb-3">
                              {getDiagnosis().issues.map((issue, i) => (
                                <li key={i} className={`text-sm flex items-start gap-2 ${
                                  issue.severity === 'critical' ? 'text-red-600' :
                                  issue.severity === 'warning' ? 'text-amber-600' : 'text-blue-600'
                                }`}>
                                  <span>{issue.icon}</span>
                                  <span>{issue.message}</span>
                                </li>
                              ))}
                            </ul>
                            {getDiagnosis().recommendations?.length > 0 && (
                              <>
                                <p className="text-sm font-medium text-slate-700 mb-1">💡 Recommendations:</p>
                                <ul className="space-y-1">
                                  {getDiagnosis().recommendations.map((rec, i) => (
                                    <li key={i} className="text-sm text-slate-600">• {rec}</li>
                                  ))}
                                </ul>
                              </>
                            )}
                          </div>
                        )}
                        
                        {/* All Good Message */}
                        {getDiagnosis()?.status === 'healthy' && (
                          <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                            <p className="text-sm text-emerald-700 flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4" />
                              All systems operational. Processing is ready to run.
                            </p>
                          </div>
                        )}
                        
                        {/* Quick Actions */}
                        <div className="flex flex-wrap gap-2 pt-2 border-t">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              try {
                                await clearStopFlag();
                                await resetStuckCalls();
                                toast.success('All flags cleared!');
                                fetchHealthFlags();
                                fetchProcessingStatus();
                              } catch (error) {
                                toast.error('Failed to clear flags');
                              }
                            }}
                            className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Clear All Flags
                          </Button>
                          
                          {healthFlags.admin_config_flags?.stop_processing && (
                            <Button
                              size="sm"
                              onClick={async () => {
                                try {
                                  await clearStopFlag();
                                  toast.success('Stop flag cleared!');
                                  fetchHealthFlags();
                                } catch (error) {
                                  toast.error('Failed to clear stop flag');
                                }
                              }}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Clear Stop Flag
                            </Button>
                          )}
                        </div>
                        
                        {/* Raw Data (collapsible for debugging) */}
                        <details className="text-xs">
                          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                            View Raw Flag Data
                          </summary>
                          <pre className="mt-2 p-2 bg-slate-100 rounded text-xs overflow-x-auto">
                            {JSON.stringify(healthFlags, null, 2)}
                          </pre>
                        </details>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Unable to load health flags
                      </p>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* SECTION 4: Troubleshooting (Collapsible) */}
              <Card className="card-elevated">
                <CardHeader className="pb-3 cursor-pointer" onClick={() => setShowTroubleshoot(!showTroubleshoot)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Settings className="h-5 w-5 text-slate-600" />
                      <CardTitle className="text-base">Troubleshooting</CardTitle>
                    </div>
                    <ChevronRight className={`h-5 w-5 text-muted-foreground transition-transform ${showTroubleshoot ? 'rotate-90' : ''}`} />
                  </div>
                </CardHeader>
                {showTroubleshoot && (
                  <CardContent className="pt-0 space-y-3">
                    <Button 
                      variant="outline"
                      onClick={handleDiagnose}
                      disabled={isDiagnosing}
                      className="w-full justify-start"
                    >
                      {isDiagnosing ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 mr-2" />
                      )}
                      Run Diagnostics
                    </Button>
                    
                    {/* Diagnosis Results */}
                    {diagnosisResult && (
                      <div className={`p-3 rounded-lg ${diagnosisResult.issues?.length > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-medium ${diagnosisResult.issues?.length > 0 ? 'text-red-700' : 'text-green-700'}`}>
                            {diagnosisResult.issues?.length > 0 ? `⚠️ ${diagnosisResult.issues.length} Issue(s)` : '✅ Healthy'}
                          </span>
                          <Button variant="ghost" size="sm" onClick={() => setDiagnosisResult(null)}>×</Button>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <span>{diagnosisResult.is_connected ? '✅' : '❌'} Connected</span>
                          <span>{diagnosisResult.token_valid ? '✅' : '❌'} Token Valid</span>
                          <span>{diagnosisResult.selected_folder_id ? '✅' : '❌'} Folder Set</span>
                        </div>
                        
                        {diagnosisResult.issues?.length > 0 && (
                          <ul className="mt-2 text-xs text-red-600 space-y-1">
                            {diagnosisResult.issues.map((issue, idx) => (
                              <li key={idx}>• {issue}</li>
                            ))}
                          </ul>
                        )}
                        
                        {diagnosisResult.has_refresh_token && !diagnosisResult.token_valid && (
                          <Button size="sm" variant="outline" onClick={handleForceRefresh} className="mt-2 w-full">
                            <RefreshCw className="h-3 w-3 mr-2" />
                            Refresh Token
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* SECTION 5: Folder Browser */}
              <Card className="card-elevated">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Folder className="h-5 w-5 text-blue-600" />
                      <CardTitle className="text-base">Select Root Folder</CardTitle>
                    </div>
                  </div>
                  <CardDescription>
                    Choose the folder containing your call recordings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Breadcrumbs */}
                  <div className="flex items-center gap-1 mb-3 p-2 bg-slate-50 rounded-lg overflow-x-auto text-sm">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleBreadcrumbClick(-1)}
                      className="shrink-0 h-7 px-2"
                    >
                      <HardDrive className="h-3 w-3 mr-1" />
                      Root
                    </Button>
                    {currentPath.map((item, idx) => (
                      <div key={item.id} className="flex items-center shrink-0">
                        <ChevronRight className="h-3 w-3 text-muted-foreground" />
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleBreadcrumbClick(idx)}
                          className="h-7 px-2"
                        >
                          {item.name}
                        </Button>
                      </div>
                    ))}
                  </div>

                  {/* Folder List */}
                  <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                    {folders.length > 0 ? folders.map((folder) => {
                      const syncInfo = folderSyncStatus[folder.id];
                      const statusColor = syncInfo?.status === 'synced' ? 'border-l-green-500 bg-green-50/30' 
                        : syncInfo?.status === 'partial' ? 'border-l-orange-500 bg-orange-50/30'
                        : syncInfo?.status === 'not_synced' ? 'border-l-red-500 bg-red-50/30'
                        : '';
                      
                      return (
                        <div 
                          key={folder.id}
                          className={`flex items-center justify-between p-2 hover:bg-muted/50 transition-colors border-l-4 ${statusColor || 'border-l-transparent'}`}
                        >
                          <button
                            onClick={() => handleFolderClick(folder)}
                            className="flex items-center gap-2 flex-1 text-left"
                          >
                            <FolderOpen className={`h-4 w-4 ${
                              syncInfo?.status === 'synced' ? 'text-green-600' 
                              : syncInfo?.status === 'partial' ? 'text-orange-500'
                              : syncInfo?.status === 'not_synced' ? 'text-red-500'
                              : 'text-blue-500'
                            }`} />
                            <div>
                              <p className="font-medium text-sm">{folder.name}</p>
                              <p className="text-xs text-muted-foreground">{folder.child_count} items</p>
                            </div>
                          </button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSelectFolder(folder)}
                            className="shrink-0 h-7"
                          >
                            <Check className="h-3 w-3" />
                          </Button>
                        </div>
                      );
                    }) : (
                      <div className="p-6 text-center text-muted-foreground">
                        <Folder className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No folders found</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Sync History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card className="card-elevated">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-sm">
                    <History className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Sync History</CardTitle>
                    <CardDescription>
                      View past OneDrive sync operations and their results
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={syncHistoryFilter} onValueChange={setSyncHistoryFilter}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Syncs</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="completed_with_errors">With Errors</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchSyncHistory(1)}
                    disabled={isLoadingSyncHistory}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingSyncHistory ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleClearSyncHistory(30)}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear Old
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary Cards */}
              {syncHistorySummary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-foreground">{syncHistorySummary.total_syncs}</div>
                    <div className="text-sm text-muted-foreground">Total Syncs</div>
                  </div>
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-600">{syncHistorySummary.successful_syncs}</div>
                    <div className="text-sm text-muted-foreground">Successful</div>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-red-600">{syncHistorySummary.failed_syncs}</div>
                    <div className="text-sm text-muted-foreground">Failed</div>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{syncHistorySummary.total_files_synced?.toLocaleString()}</div>
                    <div className="text-sm text-muted-foreground">Files Synced</div>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-amber-600">{formatDuration(syncHistorySummary.average_duration_seconds)}</div>
                    <div className="text-sm text-muted-foreground">Avg Duration</div>
                  </div>
                </div>
              )}

              {/* History Table */}
              {isLoadingSyncHistory ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : syncHistory.length === 0 ? (
                <div className="text-center py-12">
                  <History className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground">No sync history found</p>
                  <p className="text-sm text-muted-foreground mt-1">Run a sync to see history here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {syncHistory.map((sync) => (
                    <div
                      key={sync.id}
                      className="border rounded-lg overflow-hidden"
                    >
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() => setExpandedSyncId(expandedSyncId === sync.id ? null : sync.id)}
                      >
                        <div className="flex items-center gap-4">
                          {/* Status Icon */}
                          <div className={`p-2 rounded-full ${
                            sync.status === 'completed' ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                            sync.status === 'completed_with_errors' ? 'bg-amber-100 dark:bg-amber-900/30' :
                            'bg-red-100 dark:bg-red-900/30'
                          }`}>
                            {sync.status === 'completed' ? (
                              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                            ) : sync.status === 'completed_with_errors' ? (
                              <AlertTriangle className="h-5 w-5 text-amber-600" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-600" />
                            )}
                          </div>
                          
                          {/* Sync Info */}
                          <div>
                            <div className="font-medium text-foreground">
                              {sync.folder_path || 'OneDrive'}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {formatDateTime(sync.started_at)}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-6">
                          {/* Stats */}
                          <div className="flex items-center gap-4 text-sm">
                            <div className="text-center">
                              <div className="font-semibold text-emerald-600">{sync.stats?.files_synced || 0}</div>
                              <div className="text-xs text-muted-foreground">Synced</div>
                            </div>
                            <div className="text-center">
                              <div className="font-semibold text-muted-foreground">{sync.stats?.files_skipped || 0}</div>
                              <div className="text-xs text-muted-foreground">Skipped</div>
                            </div>
                            {(sync.stats?.error_count || 0) > 0 && (
                              <div className="text-center">
                                <div className="font-semibold text-red-600">{sync.stats?.error_count || 0}</div>
                                <div className="text-xs text-muted-foreground">Errors</div>
                              </div>
                            )}
                            <div className="text-center">
                              <div className="font-semibold text-blue-600">{formatDuration(sync.duration_seconds)}</div>
                              <div className="text-xs text-muted-foreground">Duration</div>
                            </div>
                          </div>

                          {/* Status Badge */}
                          <Badge className={
                            sync.status === 'completed' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' :
                            sync.status === 'completed_with_errors' ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' :
                            'bg-red-100 text-red-700 hover:bg-red-100'
                          }>
                            {sync.status === 'completed' ? 'Completed' :
                             sync.status === 'completed_with_errors' ? 'With Errors' :
                             'Failed'}
                          </Badge>

                          {/* Expand Icon */}
                          <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${expandedSyncId === sync.id ? 'rotate-180' : ''}`} />
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {expandedSyncId === sync.id && (
                        <div className="border-t bg-muted/20 p-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div>
                              <div className="text-xs text-muted-foreground uppercase tracking-wide">Started</div>
                              <div className="text-sm font-medium">{formatDateTime(sync.started_at)}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground uppercase tracking-wide">Completed</div>
                              <div className="text-sm font-medium">{formatDateTime(sync.completed_at)}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground uppercase tracking-wide">Files Found</div>
                              <div className="text-sm font-medium">{sync.stats?.files_found || 0}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground uppercase tracking-wide">Folders Scanned</div>
                              <div className="text-sm font-medium">{sync.stats?.folders_scanned || 0}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground uppercase tracking-wide">Duplicates Found</div>
                              <div className="text-sm font-medium">{sync.stats?.duplicates_found || 0}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground uppercase tracking-wide">Initiated By</div>
                              <div className="text-sm font-medium">{sync.initiated_by || 'system'}</div>
                            </div>
                          </div>

                          {/* Errors Section */}
                          {sync.errors && sync.errors.length > 0 && (
                            <div className="mt-4">
                              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                                Errors ({sync.errors.length})
                              </div>
                              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 max-h-40 overflow-y-auto">
                                {sync.errors.slice(0, 10).map((error, idx) => (
                                  <div key={idx} className="text-sm text-red-700 dark:text-red-300 py-1">
                                    <FileX className="h-3 w-3 inline mr-2" />
                                    {error}
                                  </div>
                                ))}
                                {sync.errors.length > 10 && (
                                  <div className="text-sm text-red-600 dark:text-red-400 mt-2 italic">
                                    ... and {sync.errors.length - 10} more errors
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {syncHistoryPagination.total_pages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {syncHistory.length} of {syncHistoryPagination.total} syncs
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchSyncHistory(syncHistoryPagination.page - 1)}
                      disabled={syncHistoryPagination.page <= 1 || isLoadingSyncHistory}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground px-2">
                      Page {syncHistoryPagination.page} of {syncHistoryPagination.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchSyncHistory(syncHistoryPagination.page + 1)}
                      disabled={syncHistoryPagination.page >= syncHistoryPagination.total_pages || isLoadingSyncHistory}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Guardian Tab - Self-Repair System */}
        <TabsContent value="guardian" className="space-y-6">
          {/* Health Status Card */}
          <Card className="card-elevated">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${guardianHealth?.status === 'healthy' ? 'bg-emerald-100' : guardianHealth?.status === 'degraded' ? 'bg-amber-100' : 'bg-red-100'}`}>
                    <HeartPulse className={`h-5 w-5 ${guardianHealth?.status === 'healthy' ? 'text-emerald-600' : guardianHealth?.status === 'degraded' ? 'text-amber-600' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      Processing Guardian
                      {getHealthStatusBadge(guardianHealth?.status)}
                    </CardTitle>
                    <CardDescription>
                      Self-repairing system for call processing
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchGuardianData}
                    disabled={isLoadingGuardian}
                  >
                    {isLoadingGuardian ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    onClick={handleTriggerRepair}
                    disabled={isRepairingGuardian}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isRepairingGuardian ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Repairing...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        Run Repair
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingGuardian && !guardianHealth ? (
                <div className="space-y-4">
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Status Distribution */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {guardianMetrics?.status_distribution && Object.entries(guardianMetrics.status_distribution).map(([status, count]) => (
                      <div
                        key={status}
                        className={`p-4 rounded-lg border ${
                          status === 'completed' ? 'bg-emerald-50 border-emerald-200' :
                          status === 'pending' ? 'bg-blue-50 border-blue-200' :
                          status === 'processing' ? 'bg-amber-50 border-amber-200' :
                          status === 'failed' ? 'bg-red-50 border-red-200' :
                          status === 'quarantined' ? 'bg-purple-50 border-purple-200' :
                          'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="text-2xl font-bold">{count}</div>
                        <div className="text-sm text-slate-600 capitalize">{status}</div>
                      </div>
                    ))}
                  </div>

                  {/* Guardian Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-lg bg-slate-50 border">
                      <div className="flex items-center gap-2 mb-2">
                        <RotateCcw className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium">Calls Recovered</span>
                      </div>
                      <div className="text-2xl font-bold">{guardianMetrics?.guardian_stats?.calls_recovered || 0}</div>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50 border">
                      <div className="flex items-center gap-2 mb-2">
                        <Archive className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-medium">Quarantined</span>
                      </div>
                      <div className="text-2xl font-bold">{guardianMetrics?.guardian_stats?.calls_quarantined || 0}</div>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50 border">
                      <div className="flex items-center gap-2 mb-2">
                        <RefreshCw className="h-4 w-4 text-amber-600" />
                        <span className="text-sm font-medium">Total Retries</span>
                      </div>
                      <div className="text-2xl font-bold">{guardianMetrics?.guardian_stats?.total_retries || 0}</div>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50 border">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        <span className="text-sm font-medium">Errors Caught</span>
                      </div>
                      <div className="text-2xl font-bold">{guardianMetrics?.guardian_stats?.errors_caught || 0}</div>
                    </div>
                  </div>

                  {/* Last 24h Stats */}
                  {guardianMetrics?.last_24h && (
                    <div className="p-4 rounded-lg bg-gradient-to-r from-slate-50 to-slate-100 border">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Activity className="h-4 w-4" />
                        Last 24 Hours
                      </h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <div className="text-sm text-slate-600">Completed</div>
                          <div className="text-xl font-bold text-emerald-600">{guardianMetrics.last_24h.completed}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-600">Failed</div>
                          <div className="text-xl font-bold text-red-600">{guardianMetrics.last_24h.failed}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-600">Success Rate</div>
                          <div className={`text-xl font-bold ${guardianMetrics.last_24h.success_rate >= 90 ? 'text-emerald-600' : guardianMetrics.last_24h.success_rate >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                            {guardianMetrics.last_24h.success_rate}%
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Issues Found */}
                  {guardianHealth?.issues_found?.length > 0 && (
                    <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                      <h4 className="font-medium mb-2 flex items-center gap-2 text-amber-800">
                        <AlertTriangle className="h-4 w-4" />
                        Issues Detected
                      </h4>
                      <ul className="space-y-1">
                        {guardianHealth.issues_found.map((issue, i) => (
                          <li key={i} className="text-sm text-amber-700">• {issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Actions Taken */}
                  {guardianHealth?.actions_taken?.length > 0 && (
                    <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                      <h4 className="font-medium mb-2 flex items-center gap-2 text-emerald-800">
                        <CheckCircle2 className="h-4 w-4" />
                        Actions Taken
                      </h4>
                      <ul className="space-y-1">
                        {guardianHealth.actions_taken.map((action, i) => (
                          <li key={i} className="text-sm text-emerald-700">• {action}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quarantined Calls Card */}
          <Card className="card-elevated">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Archive className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Quarantined Calls</CardTitle>
                  <CardDescription>
                    Calls that failed after multiple retries
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {quarantinedCalls.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-400" />
                  <p>No quarantined calls</p>
                  <p className="text-sm">All calls are processing normally</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Filename</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Retries</TableHead>
                      <TableHead>Quarantined At</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quarantinedCalls.map((call) => (
                      <TableRow key={call.id}>
                        <TableCell className="font-medium max-w-[200px] truncate">
                          {call.filename || call.id}
                        </TableCell>
                        <TableCell className="max-w-[300px]">
                          <span className="text-sm text-red-600 line-clamp-2">
                            {call.quarantine_reason || 'Unknown error'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{call.retry_count || 0} retries</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {formatDateTime(call.quarantined_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewAuditTrail(call.id)}
                            >
                              <Info className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnquarantineCall(call.id)}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Retry
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Recent Health Reports */}
          {guardianMetrics?.recent_health_reports?.length > 0 && (
            <Card className="card-elevated">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <BarChart3 className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Recent Health Checks</CardTitle>
                    <CardDescription>
                      Last 10 health reports
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {guardianMetrics.recent_health_reports.slice(0, 5).map((report, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`h-2 w-2 rounded-full ${getHealthStatusColor(report.status)}`} />
                        <span className="text-sm font-medium capitalize">{report.status}</span>
                        <span className="text-sm text-slate-500">
                          {formatDateTime(report.timestamp)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        {report.issues_found?.length > 0 && (
                          <span className="text-amber-600">{report.issues_found.length} issues</span>
                        )}
                        {report.actions_taken?.length > 0 && (
                          <span className="text-emerald-600">{report.actions_taken.length} actions</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Azure Configuration Tab - Only shown if user has access */}
        {!isAzureConfigRestricted && (
        <TabsContent value="configuration" className="space-y-6">
          <Card className="card-elevated">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-sm">
                  <Key className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Azure AD Configuration</CardTitle>
                  <CardDescription>
                    Configure your Azure AD app credentials for OneDrive integration
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Configuration Status */}
              <div className={`p-4 rounded-lg ${azureConfigLoaded?.is_configured ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-center gap-2">
                  {azureConfigLoaded?.is_configured ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      <span className="font-medium text-emerald-700">Azure credentials are configured</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-5 w-5 text-amber-600" />
                      <span className="font-medium text-amber-700">Azure credentials not configured</span>
                    </>
                  )}
                </div>
                {azureConfigLoaded?.is_configured && azureConfigLoaded?.client_secret_masked && (
                  <p className="text-sm text-emerald-600 mt-1">
                    Client Secret: {azureConfigLoaded.client_secret_masked}
                  </p>
                )}
              </div>

              {/* Configuration Form */}
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="client_id">Client ID (Application ID) *</Label>
                  <Input
                    id="client_id"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={azureConfig.client_id}
                    onChange={(e) => setAzureConfig({ ...azureConfig, client_id: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">Found in Azure Portal → App registrations → Overview</p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="client_secret">Client Secret (Value) *</Label>
                  <div className="relative">
                    <Input
                      id="client_secret"
                      type={showClientSecret ? 'text' : 'password'}
                      placeholder={azureConfigLoaded?.has_client_secret ? '••••••••••••••••' : 'Enter client secret value'}
                      value={azureConfig.client_secret}
                      onChange={(e) => setAzureConfig({ ...azureConfig, client_secret: e.target.value })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setShowClientSecret(!showClientSecret)}
                    >
                      {showClientSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Found in Azure Portal → App registrations → Certificates & secrets → Client secrets (use the Value, not Secret ID)
                  </p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="tenant_id">Tenant ID (Directory ID)</Label>
                  <Input
                    id="tenant_id"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx or 'common'"
                    value={azureConfig.tenant_id}
                    onChange={(e) => setAzureConfig({ ...azureConfig, tenant_id: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">Use your tenant ID for single-tenant apps, or &apos;common&apos; for multi-tenant</p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="redirect_uri">Redirect URI *</Label>
                  <Input
                    id="redirect_uri"
                    placeholder="https://your-domain.com/dashboard/onedrive"
                    value={azureConfig.redirect_uri}
                    onChange={(e) => setAzureConfig({ ...azureConfig, redirect_uri: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Must match exactly in Azure Portal → App registrations → Authentication → Redirect URIs (Web platform)
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={handleSaveAzureConfig}
                  disabled={isSavingConfig}
                  className="btn-primary"
                >
                  {isSavingConfig ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Configuration
                </Button>
                <Button
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={isTestingConnection || !azureConfigLoaded?.is_configured}
                >
                  {isTestingConnection ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <TestTube className="h-4 w-4 mr-2" />
                  )}
                  Test Connection
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Setup Instructions */}
          <Card className="card-elevated border-blue-200 bg-blue-50/30">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-blue-900">How to Get Azure Credentials</CardTitle>
            </CardHeader>
            <CardContent className="text-blue-800">
              <ol className="list-decimal list-inside space-y-3 text-sm">
                <li>
                  <span className="font-medium">Go to Azure Portal</span>
                  <p className="ml-5 text-blue-600">Navigate to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="underline">portal.azure.com</a> → Azure Active Directory → App registrations</p>
                </li>
                <li>
                  <span className="font-medium">Create or select an App Registration</span>
                  <p className="ml-5 text-blue-600">Click &quot;New registration&quot; or select an existing app</p>
                </li>
                <li>
                  <span className="font-medium">Copy the Client ID</span>
                  <p className="ml-5 text-blue-600">Found on the Overview page as &quot;Application (client) ID&quot;</p>
                </li>
                <li>
                  <span className="font-medium">Create a Client Secret</span>
                  <p className="ml-5 text-blue-600">Go to &quot;Certificates & secrets&quot; → &quot;New client secret&quot; → Copy the <strong>Value</strong> (not Secret ID)</p>
                </li>
                <li>
                  <span className="font-medium">Add Redirect URI</span>
                  <p className="ml-5 text-blue-600">Go to &quot;Authentication&quot; → &quot;Add a platform&quot; → &quot;Web&quot; → Enter your redirect URI</p>
                </li>
                <li>
                  <span className="font-medium">Add API Permissions</span>
                  <p className="ml-5 text-blue-600">Go to &quot;API permissions&quot; → &quot;Add a permission&quot; → &quot;Microsoft Graph&quot; → &quot;Delegated permissions&quot; → Add: Files.Read.All, Files.ReadWrite.All, offline_access</p>
                </li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
      
      {/* Folder Info Dialog */}
      <Dialog open={folderInfoDialog} onOpenChange={setFolderInfoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Folder className="h-5 w-5 text-blue-500" />
              {folderToSelect?.name || 'Folder'} Details
            </DialogTitle>
            <DialogDescription>
              Review folder contents before selecting for sync
            </DialogDescription>
          </DialogHeader>
          
          {isLoadingFolderInfo ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : selectedFolderInfo?.error ? (
            <div className="p-4 bg-red-50 rounded-lg text-red-600 text-center">
              {selectedFolderInfo.error}
            </div>
          ) : selectedFolderInfo && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <Files className="h-6 w-6 mx-auto text-blue-500 mb-1" />
                  <p className="text-2xl font-bold text-blue-700">{selectedFolderInfo.total_files}</p>
                  <p className="text-sm text-blue-600">Total Files</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <FileAudio className="h-6 w-6 mx-auto text-emerald-500 mb-1" />
                  <p className="text-2xl font-bold text-emerald-700">{selectedFolderInfo.audio_files}</p>
                  <p className="text-sm text-emerald-600">Audio Files</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <Folder className="h-6 w-6 mx-auto text-amber-500 mb-1" />
                  <p className="text-2xl font-bold text-amber-700">{selectedFolderInfo.folders_count}</p>
                  <p className="text-sm text-amber-600">Sub-folders</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg text-center">
                  <HardDrive className="h-6 w-6 mx-auto text-purple-500 mb-1" />
                  <p className="text-2xl font-bold text-purple-700">{selectedFolderInfo.size_display}</p>
                  <p className="text-sm text-purple-600">Total Size</p>
                </div>
              </div>
              
              {selectedFolderInfo.audio_files > 0 && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">
                    ✓ Found {selectedFolderInfo.audio_files} audio files that can be synced and processed
                  </p>
                </div>
              )}
              
              {selectedFolderInfo.audio_files === 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700">
                    ⚠️ No audio files found in this folder. Make sure your call recordings are in supported formats (.mp3, .wav, .m4a, etc.)
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderInfoDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmFolderSelection}
              disabled={isLoadingFolderInfo || selectedFolderInfo?.error}
            >
              <Check className="h-4 w-4 mr-2" />
              Select This Folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Calls List Dialog */}
      <Dialog open={callsListDialog} onOpenChange={setCallsListDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              {callsListFilter ? `${callsListFilter.charAt(0).toUpperCase() + callsListFilter.slice(1)} Calls` : 'All Calls'}
            </DialogTitle>
            <DialogDescription>
              {callsList.length} call(s) found
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {isLoadingCalls ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : callsList.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No calls found with this status
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Filename</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Status</TableHead>
                    {callsListFilter === 'failed' && <TableHead>Error</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {callsList.map((call) => (
                    <TableRow 
                      key={call.id} 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => {
                        setCallsListDialog(false);
                        navigate(`/dashboard/call/${call.id}`);
                      }}
                    >
                      <TableCell className="font-medium max-w-[200px] truncate">{call.filename || 'Unknown'}</TableCell>
                      <TableCell>{call.call_date ? new Date(call.call_date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>
                        {call.duration ? `${Math.floor(call.duration / 60)}:${String(call.duration % 60).padStart(2, '0')}` : '00:00'}
                      </TableCell>
                      <TableCell>
                        {call.average_score ? (
                          <span className={`font-medium ${call.average_score >= 7 ? 'text-green-600' : call.average_score >= 5 ? 'text-amber-600' : 'text-red-600'}`}>
                            {call.average_score.toFixed(1)}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          call.processing_status === 'completed' ? 'default' :
                          call.processing_status === 'processing' ? 'secondary' :
                          call.processing_status === 'failed' ? 'destructive' : 'outline'
                        }>
                          {call.processing_status || 'pending'}
                        </Badge>
                      </TableCell>
                      {callsListFilter === 'failed' && (
                        <TableCell className="max-w-[300px]">
                          <span className="text-sm text-red-600 truncate block" title={call.processing_error || 'Unknown error'}>
                            {call.processing_error || 'Unknown error'}
                          </span>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Trail Dialog */}
      <Dialog open={auditTrailDialog} onOpenChange={setAuditTrailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              Call Processing Audit Trail
            </DialogTitle>
            <DialogDescription>
              Complete processing history for this call
            </DialogDescription>
          </DialogHeader>
          
          {isLoadingAuditTrail ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : selectedCallAudit && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Call Info */}
              {selectedCallAudit.call && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-2">Call Details</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500">Filename:</span>
                      <span className="ml-2 font-medium">{selectedCallAudit.call.filename || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Status:</span>
                      <Badge 
                        className={`ml-2 ${
                          selectedCallAudit.call.processing_status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          selectedCallAudit.call.processing_status === 'quarantined' ? 'bg-purple-100 text-purple-700' :
                          selectedCallAudit.call.processing_status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {selectedCallAudit.call.processing_status}
                      </Badge>
                    </div>
                    <div>
                      <span className="text-slate-500">Retries:</span>
                      <span className="ml-2 font-medium">{selectedCallAudit.call.retry_count || 0}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Created:</span>
                      <span className="ml-2 font-medium">{formatDateTime(selectedCallAudit.call.created_at)}</span>
                    </div>
                  </div>
                  {selectedCallAudit.call.processing_error && (
                    <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-600">
                      <span className="font-medium">Error: </span>
                      {selectedCallAudit.call.processing_error}
                    </div>
                  )}
                  {selectedCallAudit.call.quarantine_reason && (
                    <div className="mt-2 p-2 bg-purple-50 rounded text-sm text-purple-600">
                      <span className="font-medium">Quarantine Reason: </span>
                      {selectedCallAudit.call.quarantine_reason}
                    </div>
                  )}
                </div>
              )}

              {/* Audit Trail Events */}
              <div>
                <h4 className="font-medium mb-3">Processing Events</h4>
                {selectedCallAudit.audit_trail?.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">No audit events recorded</p>
                ) : (
                  <div className="space-y-2">
                    {selectedCallAudit.audit_trail?.map((event, i) => (
                      <div
                        key={i}
                        className={`p-3 rounded-lg border ${
                          event.event_type.includes('completed') ? 'bg-emerald-50 border-emerald-200' :
                          event.event_type.includes('failed') || event.event_type.includes('quarantine') ? 'bg-red-50 border-red-200' :
                          event.event_type.includes('started') ? 'bg-blue-50 border-blue-200' :
                          'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm capitalize">
                            {event.event_type.replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs text-slate-500">
                            {formatDateTime(event.timestamp)}
                          </span>
                        </div>
                        {event.duration_ms && (
                          <span className="text-xs text-slate-600">
                            Duration: {event.duration_ms}ms
                          </span>
                        )}
                        {event.error && (
                          <p className="text-sm text-red-600 mt-1">{event.error}</p>
                        )}
                        {event.details && Object.keys(event.details).length > 0 && (
                          <div className="mt-1 text-xs text-slate-600">
                            {Object.entries(event.details).map(([key, value]) => (
                              <span key={key} className="mr-3">
                                {key}: <span className="font-medium">{String(value)}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditTrailDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Processing Status Viewer */}
      <ProcessingStatusViewer
        open={showProcessingViewer && !isProcessingViewerMinimized}
        onClose={() => setShowProcessingViewer(false)}
        isMinimized={isProcessingViewerMinimized}
        onMinimize={() => setIsProcessingViewerMinimized(!isProcessingViewerMinimized)}
        autoRefresh={processingStatus?.is_processing}
        refreshInterval={2000}
      />

      {/* Minimized Processing Viewer */}
      {showProcessingViewer && isProcessingViewerMinimized && (
        <ProcessingStatusViewer
          open={false}
          onClose={() => setShowProcessingViewer(false)}
          isMinimized={true}
          onMinimize={() => setIsProcessingViewerMinimized(false)}
        />
      )}
    </div>
  );
}
